import { useCallback } from 'react';
import type { SyntheticEvent } from 'react';
import { Form } from 'formik';

import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { ConfirmBeforeExitForm } from '../../../components/ConfirmBeforeExitForm';
import { FORM_LAYOUT_STYLE, FormLayout } from '../../../components/FormLayout';
import { MemoBeforeSubmitForm } from '../../MemoBeforeSubmit';
import type { useMemos } from '../../MemoBeforeSubmit/hooks/useMemos.hook';
import { memoAwareSubmitHandler } from '../../MemoBeforeSubmit/utils/memo-aware-submit-handler.util';
import { ReorderableCard } from '../../../components/DraggableList/ReorderableCard';
import { ItemTypes } from '../../../components/DraggableList/utils/item-types.util';
import {
  applyReorderDrag,
  isDropAfterTarget,
} from '../utils/reorder-drawer-state.util';
import type {
  ReorderDragDescriptor,
  ReorderDropDescriptor,
} from '../utils/reorder-drawer-state.util';
import { AlertMessages } from '../../../components/AlertMessages';

import { ReorderDrawerUsagesDropZone } from './UsagesDropZone';
import type { ReorderDrawerValues } from './types';

const clampUsageIndex = (atIndex: number, usagesLength: number): number =>
  atIndex > usagesLength ? usagesLength : atIndex;

interface Props {
  readonly askMemoBeforeSubmit?: boolean;
  readonly blockableCloseDrawer: () => void;
  readonly clearMemos: ReturnType<typeof useMemos>['clearMemos'];
  readonly formError: readonly string[] | null;
  readonly groupLabelById: ReadonlyMap<string, string>;
  readonly isLoading: boolean;
  readonly memos: ReturnType<typeof useMemos>['memos'];
  readonly setFieldValue: (field: string, value: unknown) => unknown;
  readonly setMemos: ReturnType<typeof useMemos>['setMemos'];
  readonly setMustBlockAction: (mustBlockAction: boolean) => void;
  readonly submitForm: () => Promise<unknown>;
  readonly submitFormEvent: (event?: SyntheticEvent<HTMLFormElement>) => void;
  readonly usageLabelById: ReadonlyMap<string, string>;
  readonly values: ReorderDrawerValues;
}

export const ReorderDrawerBody = ({
  askMemoBeforeSubmit,
  blockableCloseDrawer,
  clearMemos,
  formError,
  groupLabelById,
  isLoading,
  memos,
  setFieldValue,
  setMemos,
  setMustBlockAction,
  submitForm,
  submitFormEvent,
  usageLabelById,
  values,
}: Readonly<Props>) => {
  const isSingleGroup = values.groups.length <= 1;

  const applyDrag = useCallback(
    (drag: ReorderDragDescriptor, drop: ReorderDropDescriptor) => {
      void setFieldValue(
        'groups',
        applyReorderDrag({ drag, drop, groups: values.groups }),
      );
    },
    [setFieldValue, values.groups],
  );

  const moveGroupTo = useCallback(
    (fromIndex: number, toIndex: number) => {
      applyDrag(
        { groupIndex: fromIndex, kind: 'group' },
        { atIndex: toIndex, kind: 'group' },
      );
    },
    [applyDrag],
  );

  const moveUsageTo = useCallback(
    ({
      fromGroupIndex,
      fromUsageIndex,
      toGroupIndex,
      toUsageIndex,
    }: Readonly<{
      fromGroupIndex: number;
      fromUsageIndex: number;
      toGroupIndex: number;
      toUsageIndex: number;
    }>) => {
      applyDrag(
        {
          groupIndex: fromGroupIndex,
          kind: 'usage',
          usageIndex: fromUsageIndex,
        },
        { atIndex: toUsageIndex, groupIndex: toGroupIndex, kind: 'usage' },
      );
    },
    [applyDrag],
  );

  const openMemoDialog = useCallback(() => {
    setMemos((oldMemos) => ({ ...oldMemos, isOpenMemoDialog: true }));
  }, [setMemos]);

  const submitLabel = 'Valider';

  return (
    <Form
      onSubmit={memoAwareSubmitHandler({
        askMemo: openMemoDialog,
        mustAskMemo: !!askMemoBeforeSubmit,
        submit: submitFormEvent,
      })}
      // eslint-disable-next-line react/forbid-component-props
      style={FORM_LAYOUT_STYLE}
    >
      <ConfirmBeforeExitForm setMustBlockAction={setMustBlockAction} />
      {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-type-conversion */}
      {!!askMemoBeforeSubmit && (
        <MemoBeforeSubmitForm
          clearMemos={clearMemos}
          isLoading={isLoading}
          memos={memos}
          onConfirm={submitForm}
          setMemos={setMemos}
          submitLabel={submitLabel}
        />
      )}
      <FormLayout
        actions={
          <Stack direction="row" spacing={2}>
            <Button
              loading={isLoading}
              size="large"
              type="submit"
              variant="contained"
            >
              {submitLabel}
            </Button>
            <Button
              onClick={blockableCloseDrawer}
              size="large"
              variant="outlined"
            >
              Annuler
            </Button>
          </Stack>
        }
        error={
          !!formError && <AlertMessages messages={formError} severity="error" />
        }
        title="Réordonner les prestations"
      >
        <Grid container spacing={2}>
          {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-type-conversion */}
          {!isSingleGroup && (
            <Grid size={12}>
              <Typography sx={{ mb: 1 }} variant="subtitle2">
                Ordre des groupes
              </Typography>
              <Stack spacing={1}>
                {values.groups.map((group, groupIndex) => (
                  <ReorderableCard<ReorderDragDescriptor>
                    disableMoveDown={groupIndex === values.groups.length - 1}
                    disableMoveUp={groupIndex === 0}
                    dragItem={{ groupIndex, kind: 'group' }}
                    isDropAfter={isDropAfterTarget}
                    key={group.groupId.toString()}
                    label={
                      groupLabelById.get(group.groupId.toString()) ??
                      group.groupId.toString()
                    }
                    // eslint-disable-next-line react/jsx-no-bind
                    onDrop={(draggedItem) => {
                      if (draggedItem.kind !== 'group') {
                        return;
                      }

                      moveGroupTo(draggedItem.groupIndex, groupIndex);
                    }}
                    // eslint-disable-next-line react/jsx-no-bind
                    onMoveDown={() => {
                      moveGroupTo(groupIndex, groupIndex + 1);
                    }}
                    // eslint-disable-next-line react/jsx-no-bind
                    onMoveUp={() => {
                      moveGroupTo(groupIndex, groupIndex - 1);
                    }}
                    type={ItemTypes.GROUP}
                  />
                ))}
              </Stack>
            </Grid>
          )}
          {values.groups.map((group, groupIndex) => (
            <Grid key={group.groupId.toString()} size={12}>
              {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-type-conversion */}
              {!isSingleGroup && (
                <Typography sx={{ mb: 1 }} variant="subtitle2">
                  {groupLabelById.get(group.groupId.toString()) ??
                    group.groupId.toString()}
                </Typography>
              )}
              <ReorderDrawerUsagesDropZone
                groupIndex={groupIndex}
                isEmpty={!group.serviceUsageIds.length}
                // eslint-disable-next-line react/jsx-no-bind
                onDrop={(draggedItem, atIndex) => {
                  if (draggedItem.kind !== 'usage') {
                    return;
                  }

                  moveUsageTo({
                    fromGroupIndex: draggedItem.groupIndex,
                    fromUsageIndex: draggedItem.usageIndex,
                    toGroupIndex: groupIndex,
                    toUsageIndex: clampUsageIndex(
                      atIndex,
                      group.serviceUsageIds.length,
                    ),
                  });
                }}
              >
                {group.serviceUsageIds.map((serviceUsageId, usageIndex) => (
                  <ReorderableCard<ReorderDragDescriptor>
                    disableMoveDown={
                      usageIndex === group.serviceUsageIds.length - 1
                    }
                    disableMoveUp={usageIndex === 0}
                    dragItem={{ groupIndex, kind: 'usage', usageIndex }}
                    isDropAfter={isDropAfterTarget}
                    key={serviceUsageId.toString()}
                    label={
                      usageLabelById.get(serviceUsageId.toString()) ??
                      serviceUsageId.toString()
                    }
                    // eslint-disable-next-line react/jsx-no-bind
                    onDrop={(draggedItem) => {
                      if (draggedItem.kind !== 'usage') {
                        return;
                      }

                      moveUsageTo({
                        fromGroupIndex: draggedItem.groupIndex,
                        fromUsageIndex: draggedItem.usageIndex,
                        toGroupIndex: groupIndex,
                        toUsageIndex: usageIndex,
                      });
                    }}
                    // eslint-disable-next-line react/jsx-no-bind
                    onMoveDown={() => {
                      moveUsageTo({
                        fromGroupIndex: groupIndex,
                        fromUsageIndex: usageIndex,
                        toGroupIndex: groupIndex,
                        toUsageIndex: usageIndex + 1,
                      });
                    }}
                    // eslint-disable-next-line react/jsx-no-bind
                    onMoveUp={() => {
                      moveUsageTo({
                        fromGroupIndex: groupIndex,
                        fromUsageIndex: usageIndex,
                        toGroupIndex: groupIndex,
                        toUsageIndex: usageIndex - 1,
                      });
                    }}
                    type={ItemTypes.USAGE}
                  />
                ))}
              </ReorderDrawerUsagesDropZone>
            </Grid>
          ))}
        </Grid>
      </FormLayout>
    </Form>
  );
};
